import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { ContentReferenceContext } from '@gitbook/app-database';
import type { NavigationCommentsSideSheet } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient, useHiveRevision } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
    Pagination,
    SegmentedControl,
    SidePanel,
    SidePanelBody,
    SidePanelBodyContent,
    SidePanelCloseButton,
    SidePanelExpandButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderSecondary,
    SidePanelHeaderTitle,
    Tooltip,
} from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../state';
import { APIErrorBoundary } from '../APIErrorBoundary';
import { ContentReferenceContextProvider } from '../ContentReferences';
import { CommentThreadNav } from './buttons/CommentThreadNav';
import { CommentCard } from './CommentCard';
import { CommentInput } from './CommentInput';
import { CommentCardSkeleton } from './CommentSkeleton';
import { CommentStatusFilters, FilterCommentsButton } from './FilterCommentsButton';
import { useCommentActions } from './useCommentActions';
import { useListPaginatedComments } from './useListPaginatedComments';
import { isCommentOnDeletedNodeOrPage } from './utils';

enum CommentsSidePanelTabs {
    All = 'all',
    Page = 'page',
}

const copy = {
    space: 'Leave comments on the content, or use the editor to comment on a specific block.',
    cr: 'Describe the edits in this change request and use @someone to collaborate.',
};

export type CommentsSidePanelState = {
    currentPage: string | null;
    sideSheet: NavigationCommentsSideSheet;
};

/**
 * SideSheet to display the comments on a space.
 */
export function CommentsSidePanel(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    analyticsContext: analytics.AnalyticsContextSpaceContent;

    /** Current state of the side panel to display */
    state: CommentsSidePanelState;

    /** Callback to navigate to a new state */
    onNavigate: (state: CommentsSidePanelState) => void;

    /** Callback to close the side panel */
    onClose: () => void;
}): React.ReactElement {
    const { space, changeRequest, state, onNavigate, onClose } = props;

    const { revision } = useHiveRevision();
    const currentPage =
        state.currentPage &&
        revision.pages[state.currentPage] &&
        hiveCore.isRevisionPageDocument(revision.pages[state.currentPage])
            ? (revision.pages[state.currentPage] as hiveCore.RevisionPageDocument)
            : null;

    const { sideSheet } = state;

    const [selectedTab, setSelectedTab] = useStateWithDeps<CommentsSidePanelTabs>(
        currentPage ? CommentsSidePanelTabs.Page : CommentsSidePanelTabs.All,
        [state.sideSheet.tab]
    );

    const setOpenedComment = (comment: api.Comment | undefined) => {
        onNavigate({
            currentPage: comment?.target.page?.id || null,
            sideSheet: {
                type: 'comments',
                node: comment?.target.node?.id || undefined,
                ...(comment?.id ? { comment: comment.id } : {}),
                ...(comment?.target.tableCell
                    ? {
                          tableCell: {
                              record: comment.target.tableCell.record,
                              definition: comment.target.tableCell.definition,
                          },
                      }
                    : {}),
            },
        });
    };

    const contentReferenceContext: ContentReferenceContext = {
        organization: space.organization,
        space: space.id,
        page: currentPage?.id,
        changeRequest: changeRequest?.id,
    };

    const [statusFilter, setStatusFilter] = React.useState<CommentStatusFilters>(
        CommentStatusFilters.Open
    );
    const [authorsFilter, setAuthorsFilter] = React.useState<Set<string>>(new Set());

    const filterButton = (
        <FilterCommentsButton
            spaceId={space.id}
            changeRequestId={changeRequest?.id}
            statusFilter={statusFilter}
            setStatusFilter={(status) => setStatusFilter(status)}
            authorsFilter={authorsFilter}
            setAuthorsFilter={(authors) => setAuthorsFilter(authors)}
        />
    );

    const onGoToAllComments = () => {
        setOpenedComment(undefined);
    };

    return (
        <ContentReferenceContextProvider value={contentReferenceContext}>
            <SidePanel expandable onClose={onClose}>
                {sideSheet.comment ? (
                    <React.Suspense fallback={<CommentCardSkeleton count={1} />}>
                        <SidePanelHeader>
                            <SidePanelHeaderMain>
                                <SidePanelHeaderTitle>
                                    <Heading>Comments</Heading>
                                </SidePanelHeaderTitle>
                                <SidePanelHeaderActions>
                                    {currentPage || !sideSheet.comment ? null : filterButton}
                                    <SidePanelExpandButton />
                                    <SidePanelCloseButton />
                                </SidePanelHeaderActions>
                            </SidePanelHeaderMain>
                            <SidePanelHeaderSecondary>
                                <CommentThreadNav onPress={() => onGoToAllComments()} />
                            </SidePanelHeaderSecondary>
                        </SidePanelHeader>
                        <SidePanelBody fallback={<CommentCardSkeleton count={1} />}>
                            <SidePanelBodyContent className="[&]:gap-0 [&]:p-0">
                                <OpenedCommentBody
                                    space={space}
                                    changeRequest={changeRequest}
                                    openedCommentId={sideSheet.comment}
                                    selectedTab={selectedTab}
                                />
                            </SidePanelBodyContent>
                        </SidePanelBody>
                    </React.Suspense>
                ) : (
                    <>
                        <SidePanelHeader>
                            <SidePanelHeaderMain>
                                <SidePanelHeaderTitle>
                                    <Heading>Comments</Heading>
                                </SidePanelHeaderTitle>
                                <SidePanelHeaderActions>
                                    {currentPage ? null : filterButton}
                                    <SidePanelExpandButton />
                                    <SidePanelCloseButton />
                                </SidePanelHeaderActions>
                            </SidePanelHeaderMain>
                            {currentPage ? (
                                <SidePanelHeaderSecondary className="justify-between">
                                    <SegmentedControl<CommentsSidePanelTabs>
                                        options={[
                                            {
                                                value: CommentsSidePanelTabs.Page,
                                                label: 'This page',
                                            },
                                            {
                                                value: CommentsSidePanelTabs.All,
                                                label: 'All pages',
                                            },
                                        ]}
                                        size="small"
                                        active={selectedTab}
                                        onChange={(key) => setSelectedTab(key)}
                                    />
                                    {filterButton}
                                </SidePanelHeaderSecondary>
                            ) : null}
                        </SidePanelHeader>
                        <SidePanelBody fallback={<CommentCardSkeleton count={14} />}>
                            <ListCommentsBody
                                space={space}
                                changeRequest={changeRequest}
                                currentPage={currentPage}
                                selectedTab={selectedTab}
                                statusFilter={statusFilter}
                                authorsFilter={authorsFilter}
                                onOpenComment={setOpenedComment}
                            />
                        </SidePanelBody>
                    </>
                )}
            </SidePanel>
        </ContentReferenceContextProvider>
    );
}

/**
 * Thread view: the view of the comments sidepanel when we're isolating a single comment thread.
 */
function OpenedCommentBody(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    openedCommentId: string;
    selectedTab: CommentsSidePanelTabs;
}) {
    const { space, changeRequest, openedCommentId, selectedTab } = props;

    const { data: openedComment } = useSuspenseQuery(
        changeRequest
            ? apiQuery(
                  'getCommentInChangeRequest',
                  {
                      spaceId: space.id,
                      changeRequestId: changeRequest.id,
                      commentId: openedCommentId,
                  },
                  { nullOnError: true }
              )
            : apiQuery(
                  'getCommentInSpace',
                  {
                      spaceId: space.id,
                      commentId: openedCommentId,
                  },
                  { nullOnError: true }
              )
    );

    if (!openedComment) {
        return <CommentsEmptyState selectedTab={selectedTab} isInChangeRequest={!!changeRequest} />;
    }

    return (
        <OpenedCommentThread
            space={space}
            changeRequest={changeRequest}
            showContextLink={selectedTab === CommentsSidePanelTabs.All}
            openedComment={openedComment}
        />
    );
}

/**
 * Thread view comment: the entire fetched comment thread.
 */
function OpenedCommentThread(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    showContextLink: boolean;
    openedComment: api.Comment;
}) {
    const { space, changeRequest, showContextLink, openedComment } = props;

    const commentActions = useCommentActions({ comment: openedComment });

    return (
        <>
            {/* Ensure the container is `relative` so that any focus in this container remains trapped within it and doesn't cause the entire document to shift and offset */}
            <div className="scrollbar relative flex flex-col overflow-y-auto">
                <CommentCard
                    space={space}
                    changeRequest={changeRequest}
                    comment={openedComment}
                    showContextLink={showContextLink}
                    isSelected
                    className="bg-base p-4"
                />
            </div>

            {/* Input is positioned _outside_ the scrollable container. This makes it so that when the ListViewCommentCard is not taking up the full height, the input is right below it. But if the sidepanel is scrollable, the input stays 'fixed' and not at the bottom of the scroll. */}
            {commentActions.onReply ? (
                <CommentInput
                    testID="comment.replyInput"
                    placeholder="Leave a reply..."
                    targetNode={openedComment.id}
                    onSubmit={commentActions.onReply}
                    maxRows={20}
                    className="sticky bottom-0 flex w-full items-start gap-4 border-base border-t-0.5 bg-base p-5"
                />
            ) : null}
        </>
    );
}

/**
 * All comments: the default comments sidepanel view displaying all comments in the space or change request.
 */
function ListCommentsBody(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    currentPage: hiveCore.RevisionPageDocument | null;
    selectedTab: CommentsSidePanelTabs;
    statusFilter: CommentStatusFilters;
    authorsFilter: Set<string>;
    onOpenComment: (comment: api.Comment) => void;
}) {
    const {
        space,
        changeRequest,
        currentPage,
        selectedTab,
        statusFilter,
        authorsFilter,
        onOpenComment,
    } = props;

    // Fetches all comments, filtered by page, status and author.
    const [comments, next, previous] = useListPaginatedComments({
        spaceId: space.id,
        changeRequestId: changeRequest?.id || undefined,
        pageId:
            selectedTab === CommentsSidePanelTabs.Page && currentPage?.id
                ? currentPage.id
                : undefined,
        status: statusFilter,
        authors: authorsFilter,
    });

    return (
        <>
            {/* Ensure the container is `relative` so that any focus in this container remains trapped within it and doesn't cause the entire document to shift and offset */}
            <div className="scrollbar relative flex flex-1 flex-col overflow-y-auto">
                <div className="flex w-full flex-1 flex-col gap-2.5 bg-base">
                    {comments.length === 0 ? (
                        <CommentsEmptyState
                            selectedTab={selectedTab}
                            isInChangeRequest={!!changeRequest}
                        />
                    ) : (
                        <CommentsList
                            space={space}
                            changeRequest={changeRequest}
                            comments={comments}
                            onOpenComment={onOpenComment}
                            next={next}
                            previous={previous}
                            selectedTab={selectedTab}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

function CommentsEmptyState(props: {
    selectedTab: CommentsSidePanelTabs;
    isInChangeRequest: boolean;
}): React.ReactElement {
    const { selectedTab, isInChangeRequest } = props;

    const title = (() => {
        if (selectedTab === CommentsSidePanelTabs.Page) {
            return 'No comments on this page';
        }

        if (selectedTab === CommentsSidePanelTabs.All) {
            if (isInChangeRequest) {
                return 'No comments in this change request';
            }

            return 'No comments in this space';
        }

        assertNever(selectedTab);
    })();

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Comment />
            </EmptyStateIcon>
            <Heading>{title}</Heading>
            <Description>{isInChangeRequest ? copy.cr : copy.space}</Description>
        </EmptyState>
    );
}

function CommentsList(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    comments: Array<api.Comment>;
    next: (() => void | Promise<void>) | undefined;
    previous: (() => void | Promise<void>) | undefined;
    selectedTab: CommentsSidePanelTabs;
    onOpenComment: (comment: api.Comment) => void;
}): React.ReactElement {
    const {
        space,
        changeRequest,
        comments: commentsList,
        next,
        previous,
        onOpenComment,
        selectedTab,
    } = props;

    const hiveClient = useHiveClient();
    const { revision } = useHiveRevision();

    // Sort the comments so the ones on deleted nodes/pages are always at the bottom of the list
    const comments = commentsList.slice().sort((aComment, bComment) => {
        const aIsOnDeletedNodeOrPage = isCommentOnDeletedNodeOrPage(aComment, revision, hiveClient);
        const bIsOnDeletedNodeOrPage = isCommentOnDeletedNodeOrPage(bComment, revision, hiveClient);

        // If both comments are on deleted nodes or pages, sort by postedAt
        if (aIsOnDeletedNodeOrPage && bIsOnDeletedNodeOrPage) {
            return new Date(bComment.postedAt).getTime() - new Date(aComment.postedAt).getTime();
        }

        // If only one comment is on a deleted node or page, sort it to the bottom
        if (aIsOnDeletedNodeOrPage) {
            return 1;
        }

        if (bIsOnDeletedNodeOrPage) {
            return -1;
        }

        return 0;
    });

    return (
        <div className="flex flex-1 flex-col">
            {comments.map((comment) => {
                return (
                    <APIErrorBoundary
                        fallback={{
                            404: (
                                <Tooltip label="Comment not found">
                                    <CommentCardSkeleton count={1} />
                                </Tooltip>
                            ),
                        }}
                        key={comment.id}
                    >
                        <CommentCard
                            space={space}
                            changeRequest={changeRequest}
                            comment={comment}
                            showContextLink={selectedTab === CommentsSidePanelTabs.All}
                            isSelected={false}
                            onPress={() => onOpenComment(comment)}
                            className="p-4 hover:bg-side"
                        />
                    </APIErrorBoundary>
                );
            })}

            {next || previous ? (
                <Pagination className="px-5 pb-5" previous={previous} next={next} />
            ) : null}
        </div>
    );
}
